import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import {
  ChromeHeaderData,
  ChromeSharedData,
} from '@amzn/aac-chrome-internal-api';
import { ErrorIdProvider } from '@amzn/storm-ui';
import { initHeaderState } from 'src/features/header/store/slice';
import { markComponentStart } from 'src/commons/utils/logWithTakt';
import { CHROME_APP_NAME, Components } from 'src/commons/constants';
import { performanceMark } from 'src/utils/performance-helper';
import { store } from 'src/app/store';
import { InternalChromeConfigContextProvider } from 'src/providers/InternalChromeConfigContextProvider';
import { ChromeConfigContextProvider } from 'src/providers/ChromeConfigContextProvider';
import { ConnectedChromeApp } from 'src/commons/components/ChromeApp/ChromeApp';
import { ConnectedGlobalHeader } from 'src/features/header/GlobalHeader';
import { isFromBtswWc, isFromTrim } from 'src/commons/utils';
import {
  HeaderBundleProvider,
  PageHeaderBundleProvider,
} from 'src/providers/PantherLocalizationProvider';
import { markComponentRendered } from 'src/utils/component-initialization';

export default class AXChromeGlobalHeader extends HTMLElement {
  static observedAttributes = [
    'initChromeSharedConfig',
    'initChromeHeaderConfig',
  ];
  _initChromeSharedConfigProperty = this['initChromeSharedConfig'] ?? {};
  _initChromeHeaderConfigProperty = this['initChromeHeaderConfig'] ?? {};

  constructor() {
    super();
  }

  set initChromeSharedConfig(initChromeSharedConfig: ChromeSharedData) {
    if (initChromeSharedConfig) {
      this._initChromeSharedConfigProperty = initChromeSharedConfig;
      this.updateHeaderStore();
    }
  }

  set initChromeHeaderConfig(initChromeHeaderConfig: ChromeHeaderData) {
    if (initChromeHeaderConfig) {
      this._initChromeHeaderConfigProperty = initChromeHeaderConfig;
      this.updateHeaderStore();
    }
  }

  updateHeaderStore() {
    const trimHeaderState = {
      ...this._initChromeHeaderConfigProperty,
      ...this._initChromeSharedConfigProperty,
    };
    store.dispatch(initHeaderState(trimHeaderState));
  }

  async _renderGlobalHeader() {
    if (!isFromTrim() && !isFromBtswWc()) return;
    markComponentStart(Components.GLOBAL_HEADER);
    const endMarker = performanceMark('aac-chrome-ui-global-header');

    this.updateHeaderStore();

    ReactDOM.render(
      <ErrorIdProvider id="Chrome">
        <Provider store={store}>
          <InternalChromeConfigContextProvider>
            <ChromeConfigContextProvider>
              <ConnectedChromeApp name={CHROME_APP_NAME.GlobalHeader}>
                <HeaderBundleProvider>
                  <PageHeaderBundleProvider>
                    <ConnectedGlobalHeader />
                  </PageHeaderBundleProvider>
                </HeaderBundleProvider>
              </ConnectedChromeApp>
            </ChromeConfigContextProvider>
          </InternalChromeConfigContextProvider>
        </Provider>
      </ErrorIdProvider>,
      this,
    );

    endMarker();
  }

  connectedCallback(): void {
    this._renderGlobalHeader();
    markComponentRendered('header');
  }
}

if (!customElements.get('ax-chrome-global-header')) {
  customElements.define('ax-chrome-global-header', AXChromeGlobalHeader);
}
